import { z } from "@reflag/shared/utils/zod";

import { KeyFormatSchema } from "../utils/keyFormat";
import { ok } from "../utils/types";

export const OrgIdSchema = z.string().min(1).meta({
  description: "Organization identifier",
  id: "orgId",
});

export const AppIdSchema = z.string().min(1).meta({
  description: "App identifier",
  id: "appId",
});

export const StageIdSchema = z.string().min(1).meta({
  description: "Stage identifier",
  id: "stageId",
});

export const EnvIdSchema = z.string().min(1).meta({
  description: "Environment identifier",
  id: "envId",
});

export const ReflagUserIdSchema = z.string().min(1).meta({
  description: "Reflag user identifier",
  id: "reflagUserId",
});

export const SegmentIdSchema = z.string().min(1).meta({
  description: "Segment identifier",
  id: "segmentId",
});

export const OrgHeaderSchema = z
  .object({
    id: OrgIdSchema,
    name: z.string().min(1).meta({
      description: "Organization name",
    }),
  })
  .meta({
    description: "Organization's basic information",
    id: "orgHeader",
  });

export const AppHeaderSchema = z
  .object({
    org: OrgHeaderSchema,
    id: AppIdSchema,
    name: z.string().meta({
      description: "App name",
    }),
    demo: z.boolean().meta({
      description: "Whether the app is a demo app",
    }),
    flagKeyFormat: KeyFormatSchema,
    environments: z.array(z.lazy(() => EnvironmentHeaderSchema)).meta({
      description: "Environments within the app",
    }),
  })
  .meta({
    id: "appHeader",
    description: "Basic app information",
  });

export const ReflagUserHeaderSchema = z
  .object({
    id: ReflagUserIdSchema,
    name: z.string().min(1).meta({
      description: "User's name",
    }),
    email: z.email().meta({
      description: "User's email",
    }),
    avatarUrl: z.url().optional().meta({
      description: "User's avatar URL",
    }),
  })
  .meta({
    description: "Reflag user's basic information",
    id: "reflagUserHeader",
  });

export const StageHeaderSchema = z
  .object({
    id: StageIdSchema,
    name: z.string().min(1).meta({
      description: "Stage name",
    }),
    color: z.string().min(1).max(64).meta({
      description: "Stage color (HTML color name or hex code)",
    }),
    order: z.number().nonnegative().int().meta({
      description: "Stage order",
    }),
  })
  .meta({
    description: "Stage's basic information",
    id: "stageHeader",
  });

export const EnvironmentHeaderSchema = z
  .object({
    id: EnvIdSchema,
    name: z.string().meta({
      description: "Environment name",
    }),
    isProduction: z.boolean().meta({
      description: "Whether the environment is a production environment",
    }),
    order: z.number().nonnegative().int().meta({
      description: "Environment order in the app (zero-indexed)",
    }),
    flagStateVersion: z.number().nonnegative().int().meta({
      description: "Environment version incremented when flag state changes",
    }),
  })
  .meta({
    id: "environmentHeader",
    description: "Basic environment information",
  });

export const SegmentTypeSchema = z.enum(["all", "custom"]).meta({
  description: "Segment type",
  id: "segmentType",
});

export const SegmentHeaderSchema = z
  .object({
    id: SegmentIdSchema,
    name: z.string().min(1).meta({
      description: "Segment name",
    }),
    type: SegmentTypeSchema,
  })
  .meta({
    description: "Segment's basic information",
    id: "segmentHeader",
  });

export const EnvironmentsSortByColumns = ["name", "order"] as const;
const EnvironmentsQuerySortBySchema = z.enum(EnvironmentsSortByColumns);
export type EnvironmentsQuerySortBy = z.infer<
  typeof EnvironmentsQuerySortBySchema
>;

export const EnvironmentsQuerySchema = z
  .object({
    sortOrder: z.enum(["asc", "desc"]).default("asc"),
    sortBy: EnvironmentsQuerySortBySchema.default("order"),
  })
  .strict();

export const SlackChannelSchema = z
  .object({
    id: z.string().min(1).max(128).meta({
      description: "Slack channel identifier",
      id: "slackChannelId",
    }),
    name: z.string().min(1).max(128).meta({
      description: "Slack channel name",
      id: "slackChannelName",
    }),
  })
  .meta({
    description: "Slack channel",
    id: "slackChannel",
  });

export const InheritableSlackChannelSchema = SlackChannelSchema.or(
  z.literal("inherit"),
).meta({
  description: "Slack channel or inherit",
  id: "inheritableSlackChannel",
});

export const CollectionSchema = <T extends z.ZodTypeAny>(schema: T) => {
  const entityId = schema.meta()?.id;
  const entityDescription = schema.meta()?.description;

  ok(entityId);
  ok(entityDescription);

  return z
    .object({
      data: z.array(schema).meta({
        description: `The individual items in the collection`,
      }),
    })
    .meta({
      id: `${entityId}Collection`,
      description: `Collection of ${entityDescription}`,
    });
};

export const SortOrderSchema = z.enum(["asc", "desc"]).default("asc").meta({
  description: "Sort order applied to the sorting column",
  id: "sortOrder",
});

export const SortableCollectionSchemas = <
  TResult extends z.ZodTypeAny,
  TColumn extends Extract<keyof z.infer<TResult>, string>,
>(
  schema: TResult,
  columns: [TColumn, ...TColumn[]],
  defaultColumn: TColumn,
) => {
  const entityId = schema.meta()?.id;
  const entityDescription = schema.meta()?.description;

  ok(entityId);
  ok(entityDescription);

  const sortByColumnSchema = z
    .enum(columns)
    .default(defaultColumn)
    .meta({
      id: `${entityId}SortByColumn`,
      description: `The column to sort by`,
    });

  const querySchema = z
    .object({
      sortOrder: SortOrderSchema,
      sortBy: sortByColumnSchema,
    })
    .strict()
    .meta({
      id: `${entityId}CollectionQuery`,
      description: `Query parameters for a collection of ${entityDescription}`,
    });

  const responseSchema = z
    .object({
      data: z.array(schema).meta({
        description: `The individual items in the collection`,
      }),
      sortOrder: SortOrderSchema,
      sortBy: sortByColumnSchema,
    })
    .meta({
      id: `${entityId}Collection`,
      description: `Collection of ${entityDescription}`,
    });

  return {
    sortByColumnSchema,
    querySchema,
    responseSchema,
  };
};
